import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { useNavigate } from "react-router-dom";

import KpiSegmentCard from "./KpiSegmentCard";
import KpiCompositeCard from "./KpiCompositeCard";
import InfoButton from "./InfoButton";
import { KPI_SEGMENTS } from "../../constants/clinicalDashboard";
import { HELP_CONTENT } from "../../constants/clinicalHelpContent";

/**
 * Panel 1: KPI Overview
 * Grid de 6 segment cards + 2 composite cards (Care Gaps + Coding)
 */
export default function PanelKpiOverview({ kpis, loading }) {
  const navigate = useNavigate();

  const handleSegmentClick = (segmentKey) => {
    const segmentMap = {
      assignedPatients: "/PatientMap",
      highCost: "/PatientMap?segment=highCost",
      highRisk: "/PatientMap?segment=highRisk",
      emergency: "/PatientMap?segment=emergency",
      discharged: "/PatientMap?segment=discharged",
      admitted: "/PatientMap?segment=admitted",
    };
    const path = segmentMap[segmentKey];
    if (path) navigate(path);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Panel header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              bgcolor: "#EFF6FF",
              color: "#2563EB",
            }}
          >
            <DashboardIcon sx={{ fontSize: 16 }} />
          </Box>
          <Typography variant="subtitle2" fontWeight={700}>
            KPI Overview
          </Typography>
          <InfoButton helpContent={HELP_CONTENT.kpiOverview} />
        </Stack>

        {/* Row 1: 6 Segment KPI Cards */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(6, 1fr)",
            },
            gap: 1.5,
          }}
        >
          {KPI_SEGMENTS.map((seg) => (
            <KpiSegmentCard
              key={seg.key}
              label={seg.label}
              value={kpis?.[seg.key] ?? 0}
              icon={seg.icon}
              color={seg.color}
              bgColor={seg.bgColor}
              loading={loading}
              onClick={() => handleSegmentClick(seg.key)}
            />
          ))}
        </Box>

        {/* Row 2: 2 Composite KPI Cards */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
        >
          <KpiCompositeCard
            type="careGaps"
            data={kpis}
            loading={loading}
            onClick={() => navigate("/care-gaps")}
          />
          <KpiCompositeCard
            type="coding"
            data={kpis}
            loading={loading}
            onClick={() => navigate("/coding-opportunities")}
          />
        </Stack>
      </Stack>
    </Paper>
  );
}
